import { Field, Form, Formik } from 'formik';
import type { FieldProps } from 'formik';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { object } from 'yup';

import {
  DATE_RELATIONS,
  EXCLUDED_RELATIONS,
  useDownloadOrderListCsv,
} from '@frontend/api';
import { Button, Divider } from '@frontend/components';

import {
  SalespersonFilter,
  AccountFilter,
  ContactFilter,
  EstimatorFilter,
  FiltersButtonsWrapper,
  FiltersFieldsWrapper,
  HasProcessFilter,
  DateFilter,
  getRelativeDateFilterValidationSchema,
  transformRelationFilterValue,
} from 'components/listTablePage';
import {
  onCloseFunction,
  useOnClosePopoverContext,
} from 'components/listTablePage/searchAndFilters/components/filters/context';

import { useOrderListSearchParams } from 'modules/orders/listPage/hooks';
import { useActiveTab } from 'modules/orders/listPage/hooks';
import { OrderListFilters } from 'modules/orders/listPage/types';

import { transformFiltersParamsToApi } from 'services/orderService';

import { getGroupId } from 'store/session/selectors';

import { PaymentStatusFilter } from './PaymentStatusFilter';
import { PriceFilter } from './PriceFilter';
import { INITIAL_ORDER_FILTERS } from './const';
import type {
  OrdersListFiltersFormData,
  OrdersListFiltersValues,
} from './types';

export type FiltersProps = {
  onApplyFilters: (filters: OrderListFilters) => void;
  activeFilters: OrderListFilters;
  isFetching: boolean;
};

export const transformFilterValues = (values: OrdersListFiltersFormData) => {
  const { orderedDate, ...restFiltersFormValues } = values;
  const { startDate, endDate } = orderedDate
    ? transformRelationFilterValue(orderedDate)
    : [null, null];
  const filtersToApply: OrdersListFiltersValues = {
    ...restFiltersFormValues,
    orderedDate: [startDate || null, endDate || null],
  };

  return filtersToApply;
};

export const Filters = ({
  onApplyFilters,
  activeFilters,
  isFetching,
}: FiltersProps) => {
  const groupId = useSelector(getGroupId);
  const { searchParams } = useOrderListSearchParams();
  const search = searchParams.search;
  const activeTab = useActiveTab();
  const { mutate: downloadOrderListCsv, isLoading: isExporting } =
    useDownloadOrderListCsv({ groupId });
  const exportOrders = useCallback(
    (values: OrdersListFiltersFormData) => {
      const filtersToApply = transformFilterValues(values);
      const queryVars = {
        ...transformFiltersParamsToApi(filtersToApply),
        search,
        status: activeTab !== 'all' ? activeTab : undefined,
      };
      downloadOrderListCsv(queryVars);
    },
    [downloadOrderListCsv, search, activeTab],
  );

  const onClosePopover: onCloseFunction = useOnClosePopoverContext();

  const applyFilters = ({ filters }: { filters: OrdersListFiltersValues }) => {
    onApplyFilters(filters);
    onClosePopover();
  };
  const resetFilters = () => {
    onApplyFilters(INITIAL_ORDER_FILTERS);
    onClosePopover();
  };

  const initialValues: OrdersListFiltersFormData = useMemo(() => {
    const filters = { ...activeFilters };
    return filters;
  }, [activeFilters]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(filtersFormValues: OrdersListFiltersFormData) => {
        const filtersToApply = transformFilterValues(filtersFormValues);
        applyFilters({
          filters: filtersToApply,
        });
      }}
      validationSchema={object().shape({
        orderedDate: getRelativeDateFilterValidationSchema(),
      })}
    >
      {(props) => {
        const { values } = props;
        return (
          <Form autoComplete="none">
            <FiltersFieldsWrapper>
              <AccountFilter />
              <ContactFilter />
              <SalespersonFilter />
              <EstimatorFilter />
              <Divider />
              <PriceFilter />
              <PaymentStatusFilter />
              <DateFilter
                label="Ordered"
                fieldName="orderedDate"
                excludedRelations={{
                  ...EXCLUDED_RELATIONS,
                  [DATE_RELATIONS.NEXT]: true,
                  [DATE_RELATIONS.AFTER]: true,
                  [DATE_RELATIONS.BEFORE]: true,
                  [DATE_RELATIONS.CURRENT]: true,
                  [DATE_RELATIONS.PREVIOUS]: true,
                }}
              />
              <HasProcessFilter />
            </FiltersFieldsWrapper>
            <FiltersButtonsWrapper>
              <Field>
                {({ form }: FieldProps<never, OrdersListFiltersFormData>) => (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      resetFilters();
                      form.resetForm({ values: INITIAL_ORDER_FILTERS });
                    }}
                  >
                    Reset
                  </Button>
                )}
              </Field>
              <Button type="submit" variant="primary" isLoading={isFetching}>
                Apply Filters
              </Button>
              <Button
                variant="secondary"
                isLoading={isExporting}
                onClick={() => exportOrders(values)}
              >
                Export
              </Button>
            </FiltersButtonsWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};
